import {
  AdvisoryServicesKeys,
  AdvisoryServicesProductKeys,
  ATLASSIAN_ORGANISATION,
  CloudProductKeys,
  SupportProductKeys,
} from 'data/products/keys';
import {
  LICENSE_ACADEMIC,
  LICENSE_COMMERCIAL,
  LICENSE_COMMUNITY,
  LICENSE_DEVELOPER,
  LICENSE_EVALUATION,
  LICENSE_FREE,
  LICENSE_OPENSOURCE,
  LICENSE_STARTER,
} from 'model/hams/LicenseType';
import { State } from 'model/State';
import { getCurrentContactEmail } from 'modules/auth/selectors';
import { selectSwitchedUser } from 'modules/mac/duck/selectors';
import moment from 'moment';

export const pricingPlanBambooDC = 'pricingplan.bamboo-data-center';

export const jiraAddOnKeys = [
  'com.pyxis.greenhopper.jira',
  'pricingplan.com.pyxis.greenhopper.jira',
];

export const serviceDeskAddOnKeys = [
  'com.atlassian.servicedesk',
  'pricingplan.com.atlassian.servicedesk',
];

export const sourceTreeKeys = ['sourcetree', 'pricingplan.sourcetree'];

export const noLicenseKeyValueDisplayedForKeys = [
  'pricingplan.technical-account-management',
  'pricingplan.premier-support',
  'enterprise-success-package',
  SupportProductKeys.PRIORITY_SUPPORT_SERVER,
  SupportProductKeys.PRIORITY_SUPPORT_DATA_CENTER,
  SupportProductKeys.PRIORITY_SUPPORT_DATA_CENTER_BITBUCKET,
  SupportProductKeys.PRIORITY_SUPPORT_DATA_CENTER_BAMBOO,
];

export const atlassianCloudKeys = [
  'atlassian.ondemand',
  'pricingplan.atlassian.ondemand',
  'atlassian.cloud',
];

export const notPartOfForRenewKeys = [
  SupportProductKeys.PRIORITY_SUPPORT_DATA_CENTER_BAMBOO,
  ...AdvisoryServicesProductKeys,
  pricingPlanBambooDC,
];

export const serverIdRequiredForProductTypes = [
  'PERPETUAL',
  'PERPETUAL_PRICING_PLAN',
  'BTF_SUBSCRIPTION',
  'BTF_SUBSCRIPTION_ADDON',
  'OTHER',
];

export const serverIdNotRequiredForKeys = [
  'clover',
  'pricingplan.clover',
  'sharepoint.connector',
  'pricingplan.sharepoint.connector',
  'pricingplan.sourcetree',
  'stack.data-center',
];

export const hostedAccountTypes = [
  'HOSTED',
  'HOSTED_ADDON',
  'ONDEMAND_ADDON',
  'SUBSCRIPTION',
  'SUBSCRIPTION_ADDON',
];

export const jiraKeys = [
  'jira.hosted',
  'jira.ondemand',
  'pricingplan.jira.ondemand',
  'jira',
  'pricingplan.jira',
  'jira.archiver',
  'pricingplan.jira-data-center',
  'jira-core.ondemand',
  'jira-software.ondemand',
  'jira-servicedesk.ondemand',
];
export const JiraCoreKeys = ['jira-core.ondemand', 'jira-core', 'jira-core.data-center'];
export const JiraSoftwareKeys = [
  'jira-software.ondemand',
  'jira-software',
  'jira-software.data-center',
];
export const jiraServiceDeskKeys = [
  'jira-servicedesk.ondemand',
  'jira-servicedesk',
  'jira-servicedesk.data-center',
];
export const jiraRenaissanceKeys = [...JiraCoreKeys, ...JiraSoftwareKeys, ...jiraServiceDeskKeys];

export const jiraPreRenaissanceKeys = [...jiraRenaissanceKeys, ...jiraKeys];

export const confluenceKeys = [
  'confluence.hosted.small.business',
  'confluence.hosted',
  'confluence.ondemand',
  'pricingplan.confluence.ondemand',
  'confluence',
  'pricingplan.confluence',
  'pricingplan.confluence-data-center',
];

export const bitbucketBtfKeys = ['stash', 'pricingplan.stash', 'pricingplan.stash-data-center'];

export const bambooKeys = [
  'pricingplan.bamboo.ondemand',
  'bamboo.ondemand',
  'bamboo',
  'pricingplan.bamboo',
  'bamboo-data-center',
];

export const crowdKeys = ['crowd.hosted', 'crowd', 'pricingplan.crowd', 'crowd.data-center'];

export const fisheyeKeys = ['fisheye', 'pricingplan.fisheye'];

export const addOnProductTypes = ['ADDON', 'ADDON_PRICING_PLAN'];

export const BTFProductTypes = [...serverIdRequiredForProductTypes, ...addOnProductTypes];

export const isCommercialOrAcademicOrDeveloperType = (licenseType) =>
  [LICENSE_COMMERCIAL, LICENSE_ACADEMIC, LICENSE_DEVELOPER].includes(licenseType);

export const isAdvisoryServicesProduct = (key: AdvisoryServicesKeys) =>
  AdvisoryServicesProductKeys.includes(key);

export const isNoLicenseKeyValueDisplayedForKeys = (key: string) =>
  noLicenseKeyValueDisplayedForKeys.includes(key);

export const isAddOnType = (productType) => addOnProductTypes.includes(productType);

export const isEvaluationType = (licenseType) => licenseType === LICENSE_EVALUATION;

export const isFreeType = (licenseType) => licenseType === LICENSE_FREE;

export const isCommercialOrAcademicOrFreeType = (licenseType) =>
  [LICENSE_COMMERCIAL, LICENSE_ACADEMIC, LICENSE_FREE].includes(licenseType);

export const isCommercialOrAcademicOrDeveloperOrStarterOrOpenSourceLicenseType = (licenseType) =>
  [
    LICENSE_COMMERCIAL,
    LICENSE_ACADEMIC,
    LICENSE_DEVELOPER,
    LICENSE_STARTER,
    LICENSE_OPENSOURCE,
  ].includes(licenseType);

export const isCommercialOrAcademicOrDeveloperOrStarterOrOpenSourceOrCommunityLicenseType = (
  licenseType,
) =>
  [
    LICENSE_COMMERCIAL,
    LICENSE_ACADEMIC,
    LICENSE_DEVELOPER,
    LICENSE_STARTER,
    LICENSE_OPENSOURCE,
    LICENSE_COMMUNITY,
  ].includes(licenseType);

export const isCommunityLicenseType = (licenseType) => licenseType === LICENSE_COMMUNITY;

export const isCommunityOrOpenSourceLicenseType = (licenseType) =>
  [LICENSE_COMMUNITY, LICENSE_OPENSOURCE].includes(licenseType);

export const isAcademicOrNoneLicenseType = (licenseType) =>
  [LICENSE_ACADEMIC, 'NONE'].includes(licenseType);

export const isCommercialOrAcademicType = (licenseType) =>
  [LICENSE_COMMERCIAL, LICENSE_ACADEMIC].includes(licenseType);

export const isAtlassianCloud = (productKey) => atlassianCloudKeys.includes(productKey);

export const isBitbucket = (productKey) => productKey === CloudProductKeys.BITBUCKET;

export const isAtlassianOrganisation = (key) => key === ATLASSIAN_ORGANISATION;

const nowDate = new Date();
const endDateObject = (endDate: Date) => new Date(endDate);

export const isNextMonthFutureDate = (endDate: Date) =>
  moment(endDate).add(1, 'M').isSameOrAfter(nowDate);

export const isNextMonthPastDate = (endDate: Date) => moment(endDate).add(1, 'M').isBefore(nowDate);

export const isFutureEndDate = (endDate: Date) => endDateObject(endDate) > nowDate;

export const isPastEndDate = (endDate: Date) => endDateObject(endDate) < nowDate;

export const isNotMoreThat5DaysOverdue = (endDate: Date) =>
  moment(endDate).add(5, 'd').isAfter(nowDate);

export const isInNext30Days = (endDate: Date) =>
  moment(endDate).subtract(30, 'd').isBefore(nowDate);

export const getImpersonationHeader = (state: State) => {
  const currentContactEmail = getCurrentContactEmail(state);
  const switchedUserEmail = selectSwitchedUser(state);
  return currentContactEmail === switchedUserEmail
    ? {}
    : {
        'HAMS-Impersonated-Contact-Email': switchedUserEmail,
      };
};

export const isOrderMigrationInProgress = (cartStatus: string, quoteMigrationStatus: string) =>
  cartStatus === 'DELETED' && quoteMigrationStatus?.toLowerCase() === 'inprogress';

export const isOrderMigrationCompleted = (cartStatus: string, quoteMigrationStatus: string) =>
  cartStatus === 'DELETED' && quoteMigrationStatus?.toLowerCase() === 'completed';

export const isOtherOrderErrorReason = (
  cartStatus: string,
  quoteMigrationStatus: string,
  cartOrderDetails: any,
  formData: {
    orderNumber: number | null;
    currency: { label: string; value: string };
    total: number | null;
  },
) =>
  (cartStatus === 'DELETED' && quoteMigrationStatus?.toLowerCase() !== 'completed') ||
  cartStatus !== 'QUOTED' ||
  cartOrderDetails.currency !== formData.currency.value ||
  cartOrderDetails.totalIncTax !== formData.total;

export const isHosted = (type: string) => hostedAccountTypes.includes(type);
